import { useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import usePageParams from '@shared/hooks/src/usePageParams'

import { LabDetailsModal } from '@pages/Labs/Lab'
import LinearProgress from '@components/LinearProgress'
import PaginationComp from '@components/Pagination'
import StatusChip from '@components/StatusChip'
import TableEmpty from '@components/TableEmpty'
import TableLoader from '@components/TableLoader'

import { usePatientLabs } from './PatientLabs.hooks'

export function Inline({ patientId, variant, perPage, disableRowsPerPage }) {
  const { data, isRefreshing, isPending, order, setOrder } = useLabsData({ patientId, perPage })
  const [selectedLabId, setSelectedLabId] = useState(null)

  return (
    <Stack spacing={1}>
      <Paper variant={variant} sx={{ overflow: 'hidden', position: 'relative' }}>
        <LinearProgress loading={isRefreshing} />
        <Content data={data} isLoading={isPending} order={order} onOrderChange={setOrder} onSelect={setSelectedLabId} perPage={perPage} />
      </Paper>
      <Pagination data={data} isLoading={isPending} perPage={perPage} disableRowsPerPage={disableRowsPerPage} />
      <LabDetailsModal labId={selectedLabId} open={Boolean(selectedLabId)} onClose={() => setSelectedLabId(null)} />
    </Stack>
  )
}

export function Modal({ patientId, open, onClose }) {
  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose} scroll="paper">
      <ModalContent patientId={patientId} onClose={onClose} />
    </Dialog>
  )
}

function ModalContent({ patientId, onClose }) {
  const { data, isRefreshing, isPending, order, setOrder } = useLabsData({ patientId })
  const [selectedLabId, setSelectedLabId] = useState(null)

  return (
    <>
      <DialogTitle onClose={onClose}>Labs</DialogTitle>
      <LinearProgress loading={isRefreshing} />
      <DialogContent sx={{ p: 0 }}>
        <Content data={data} isLoading={isPending} order={order} onOrderChange={setOrder} onSelect={setSelectedLabId} />
      </DialogContent>
      <DialogActions sx={{ width: '100%', justifyContent: 'flex-start' }}>
        <Pagination data={data} isLoading={isPending} />
      </DialogActions>
      <LabDetailsModal labId={selectedLabId} open={Boolean(selectedLabId)} onClose={() => setSelectedLabId(null)} />
    </>
  )
}

function useLabsData({ patientId, perPage: perPageOverride }) {
  const [order, setOrder] = useState('desc')
  const [page, queryPerPage] = usePageParams({ id: 'labshx' })
  const perPage = perPageOverride ?? queryPerPage

  const query = usePatientLabs(
    patientId,
    {
      order,
      sort: 'result_date',
      limit: perPage,
      offset: (page - 1) * perPage,
    },
    {
      enabled: Boolean(patientId),
      placeholderData: keepPreviousData,
    }
  )

  return { ...query, order, setOrder }
}

function Content({ data, isLoading, order, onOrderChange, onSelect, perPage }) {
  const showLoading = isLoading
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <TableContainer>
      <Table size="small" sx={{ '& tbody tr:last-of-type td': { borderBottom: 0 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Labwork</TableCell>
            <TableCell sortDirection={order}>
              <TableSortLabel active direction={order} onClick={() => onOrderChange(order === 'desc' ? 'asc' : 'desc')}>
                Result
              </TableSortLabel>
            </TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {showLoading && <TableLoader columns={4} items={perPage} />}
          {showEmpty && <TableEmpty columns={4} />}
          {showData && data?.map((lab) => <Lab key={lab.id} data={lab} onSelect={() => onSelect(lab.id)} />)}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function Lab({ data, onSelect }) {
  return (
    <TableRow hover onClick={onSelect} sx={{ cursor: 'pointer' }}>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
          <Typography>{data.lab}</Typography>
          <StatusChip.Lab status={data.status} noLabel />
        </Stack>
      </TableCell>
      <TableCell>{data.panelsToString}</TableCell>
      <TableCell>{data.resultDate ? dayjs(data.resultDate).format('L') : ''}</TableCell>
      <TableCell>{dayjs(data.createdAt).format('L')}</TableCell>
    </TableRow>
  )
}

function Pagination({ data, isLoading, perPage: perPageOverride, disableRowsPerPage }) {
  const [page, queryPerPage, setPageParams] = usePageParams({ id: 'labshx' })
  const perPage = perPageOverride ?? queryPerPage

  return (
    <PaginationComp
      id="labshx"
      loading={isLoading}
      last={data?.length < perPage}
      page={page}
      perPage={perPage}
      setPageParams={(params) => setPageParams(perPageOverride ? { page: params.page } : params)}
      disableRowsPerPage={disableRowsPerPage}
    />
  )
}
